import React, { useState, useEffect } from "react";
import ExternalListingButton from "../../components/ExternalListingButton";
import ListingContactButtons from "../../components/ListingContactButtons";
import ListingActionBar from "../../components/ListingActionBar";
import { T } from "../Translate";
import {
  Container,
  CardMedia,
  Box,
  Typography,
  CircularProgress,
  Dialog,
  Button,
  IconButton,
  Card,
  CardContent,
  Chip,
  Stack,
  Grid,
} from "@mui/material";
import { ArrowBackIos as ArrowBackIosIcon } from "../../icons";
import { ArrowForwardIos as ArrowForwardIosIcon } from "../../icons";
import { LocationOn as LocationOnIcon } from "../../icons";
import { PhotoLibrary as PhotoLibraryIcon } from "../../icons";
import { DirectionsCar as DirectionsCarIcon } from "../../icons";
import { Close as CloseIcon } from "../../icons";
import {
  doc,
  getDoc,
  collection,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header3 from "./CarsHeader";
import BottomNav from "../BottomToolbar";
import ListingImageSlideshow from "../../components/ListingImageSlideshow";
import FollowItemButton from "../../components/FollowItemButton";
import { isLoggedIn } from "../../session";
import { brandColors } from "../../theme";
import { pageContentSx } from "../../pageLayout";
import StoresAppBar from "../stores/StoresAppBar";
import GuestJoinBanner from "../../components/GuestJoinBanner";

const CONDITION_STYLES = {
  Nuevo: { color: "#4a90e2", bgcolor: "rgba(74,144,226,0.16)", label: <T>Nuevo</T> },
  Usado: { color: "#50e3c2", bgcolor: "rgba(80,227,194,0.16)", label: <T>Usado</T> },
  Reparar: { color: "#ed8936", bgcolor: "rgba(237,137,54,0.16)", label: <T>Para reparar</T> },
};

const CarDetail = () => {
  const { carId } = useParams();
  const [carData, setCarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [authUserId, setAuthUserId] = useState(null);
  // null = todavía no se sabe si hay sesión. La página es pública: sin
  // sesión se ve igual (con barra simple e invitación a registrarse) y solo
  // "Consultar" pide iniciar sesión.
  const [isGuest, setIsGuest] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchAuthUserId = async () => {
      const user = await isLoggedIn();
      setAuthUserId(user ? user.uid : null);
      setIsGuest(!user);
    };
    fetchAuthUserId();
  }, []);

  useEffect(() => {
    const fetchCar = async () => {
      try {
        const carDocRef = doc(db, "cars", carId);
        const carDoc = await getDoc(carDocRef);

        if (carDoc.exists()) {
          setCarData({ id: carDoc.id, ...carDoc.data() });
        } else {
          console.error("No se encontró el vehículo.");
        }
      } catch (error) {
        console.error("Error al obtener el vehículo:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCar();
  }, [carId]);

  const formatDate = (timestamp) => {
    if (timestamp && timestamp.toDate instanceof Function) {
      return timestamp.toDate().toLocaleDateString();
    }
    return "";
  };

  const handleOpenImageModal = (index = 0) => {
    setCurrentImageIndex(index);
    setOpenImageModal(true);
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === carData.images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? carData.images.length - 1 : prevIndex - 1
    );
  };

  const handleChatClick = async () => {
    if (isGuest) {
      navigate("/login", { state: { from: location } });
      return;
    }
    if (!authUserId || !carData) return;

    try {
      const conversationsRef = collection(db, "conversations");
      const conversationId = [authUserId, carData.uid].sort().join("_");
      const conversationDocRef = doc(conversationsRef, conversationId);
      const conversationDoc = await getDoc(conversationDocRef);

      if (!conversationDoc.exists()) {
        await setDoc(conversationDocRef, {
          participants: [authUserId, carData.uid],
          type: "auto",
          itemId: carData.id,
          lastMessage: {
            content: "",
            timestamp: serverTimestamp(),
            senderId: authUserId,
          },
          unreadCount: {
            [authUserId]: 0,
            [carData.uid]: 0,
          },
        });
      }

      navigate(`/chatbox/auto/${carData.id}/${carData.uid}`);
    } catch (error) {
      console.error("Error al iniciar la conversación:", error);
    }
  };

  if (loading || isGuest === null) {
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (!carData) {
    return (
      <Container maxWidth={false} disableGutters sx={pageContentSx}>
        <Typography><T>No se encontró el Vehículo.</T></Typography>
      </Container>
    );
  }

  const images = Array.isArray(carData.images) ? carData.images : [];
  const conditionStyle = CONDITION_STYLES[carData.condition];
  const isOwner = authUserId === carData.uid;

  return (
    <Box sx={{ backgroundColor: brandColors.backgroundDefault, minHeight: "100vh" }}>
      {isGuest ? (
        <StoresAppBar title={<T>Vehículo</T>} subtitle="Notiline" quickNav={false} />
      ) : (
        <Header3 />
      )}
      <Container sx={{ mt: isGuest ? 4 : 12, mb: 18, maxWidth: { xs: "100%", md: "980px" } }}>
        {isGuest && <GuestJoinBanner />}
        <Box
          onClick={() => images.length > 0 && handleOpenImageModal(0)}
          sx={{ position: "relative", width: "100%", mb: 2, cursor: images.length > 0 ? "pointer" : "default" }}
        >
          <ListingImageSlideshow
            images={images}
            alt={carData.title}
            height={{ xs: 240, sm: 320 }}
            autoPlayMs={5000}
            PlaceholderIcon={DirectionsCarIcon}
            placeholderText={<T>No hay imágenes disponibles</T>}
          />
          {images.length > 1 && (
            <Chip
              icon={<PhotoLibraryIcon sx={{ color: "white !important" }} />}
              label={<T>Ver todas</T>}
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                bgcolor: "rgba(0,0,0,0.65)",
                color: "white",
                fontWeight: 600,
              }}
            />
          )}
        </Box>

        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: "text.primary", minWidth: 0, overflowWrap: "anywhere" }}>
            {carData.title}
          </Typography>
          {conditionStyle && (
            <Chip
              size="small"
              label={conditionStyle.label}
              sx={{ flexShrink: 0, fontWeight: 600, color: conditionStyle.color, bgcolor: conditionStyle.bgcolor }}
            />
          )}
        </Stack>

        <Typography sx={{ fontSize: 26, fontWeight: 700, color: "#4CAF50", mb: 0.5 }}>
          $ {Number(carData.price).toLocaleString()}
        </Typography>

        <FollowItemButton type="car" item={{ id: carId, ...carData }} variant="button" sx={{ mb: 2 }} />

        {(carData.address || carData.city) && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 2, color: "text.secondary" }}>
            <LocationOnIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: 14 }}>{carData.address || carData.city}</Typography>
          </Stack>
        )}

        <Card sx={{ borderRadius: 3, backgroundColor: brandColors.backgroundPaper, border: "1px solid", borderColor: "divider", mb: 2 }}>
          <CardContent>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", mb: 1.5 }}>
              <T>Detalles</T>
            </Typography>
            <Grid container spacing={2}>
              {[
                { label: <T>Marca</T>, value: carData.marca },
                { label: <T>Línea</T>, value: carData.modelo },
                { label: <T>Año</T>, value: carData.año },
                { label: <T>Kilómetros</T>, value: `${carData.km} km` },
              ].map((item, i) => (
                <Grid item xs={6} key={i}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{item.label}</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.primary", overflowWrap: "anywhere" }}>
                    {item.value}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, backgroundColor: brandColors.backgroundPaper, border: "1px solid", borderColor: "divider", mb: 2 }}>
          <CardContent>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", mb: 1 }}>
              <T>Descripción</T>
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.secondary", whiteSpace: "pre-line" }}>
              {carData.description}
            </Typography>
          </CardContent>
        </Card>

        <Typography sx={{ fontSize: 11, color: "text.disabled", mb: 2 }}>
          <T>Publicado el</T> {formatDate(carData.createdAt)}
        </Typography>

        {/* Anuncio importado de otro sitio: se contacta en la publicación original. */}
        <ListingActionBar isGuest={isGuest}>
          {carData.sourceUrl ? (
            <ExternalListingButton url={carData.sourceUrl} />
          ) : (
            <>
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleChatClick}
                disabled={isOwner}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.5,
                  bgcolor: isOwner ? undefined : brandColors.action,
                  "&:hover": { bgcolor: brandColors.actionHover },
                }}
              >
                {isOwner ? <T>Este es tu anuncio</T> : <T>Consultar</T>}
              </Button>
              {!isOwner && (
                <ListingContactButtons
                  phone={carData.whatsapp}
                  title={carData.title}
                  isGuest={isGuest}
                  onRequireLogin={() => navigate("/login", { state: { from: location } })}
                />
              )}
            </>
          )}
        </ListingActionBar>
      </Container>

      <Dialog
        open={openImageModal}
        onClose={handleCloseImageModal}
        aria-label="Imágenes del vehículo"
        fullScreen
        PaperProps={{ sx: { bgcolor: "#000" } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1,
            py: 1,
            position: "sticky",
            top: 0,
            zIndex: 1,
            bgcolor: "rgba(0,0,0,0.6)",
          }}
        >
          <IconButton onClick={handleCloseImageModal} sx={{ color: "white" }} aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
          {images.length > 1 && (
            <Typography sx={{ color: "white", fontSize: 14 }}>
              {currentImageIndex + 1} / {images.length}
            </Typography>
          )}
          <Box sx={{ width: 40 }} />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            minHeight: 0,
            p: 2,
          }}
        >
          {images.length > 0 && (
            <>
              <CardMedia
                component="img"
                image={images[currentImageIndex]}
                alt={`Imagen ${currentImageIndex + 1} del vehículo`}
                sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 2 }}
              />
              {images.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePreviousImage}
                    sx={{ position: "absolute", left: 8, color: "white", bgcolor: "rgba(0,0,0,0.4)" }}
                  >
                    <ArrowBackIosIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleNextImage}
                    sx={{ position: "absolute", right: 8, color: "white", bgcolor: "rgba(0,0,0,0.4)" }}
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>
                </>
              )}
            </>
          )}
        </Box>
      </Dialog>

      {!isGuest && <BottomNav />}
    </Box>
  );
};

export default CarDetail;
